// FieldIQ — localStorage/IndexedDB persistence (chats, analytics, activity, audio)

const CONV_STORAGE_KEY = (fid, email) => `hyve-rag-chats-${fid}-${email}`;

export const loadConversations = (facilityId, email) => {
  try {
    const raw = JSON.parse(
      localStorage.getItem(CONV_STORAGE_KEY(facilityId, email)) || "[]",
    );
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(
        (c) =>
          c &&
          typeof c.id === "string" &&
          Array.isArray(c.messages) &&
          c.createdAt,
      )
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  } catch {
    return [];
  }
};

const saveConversations = (facilityId, email, conversations) => {
  localStorage.setItem(
    CONV_STORAGE_KEY(facilityId, email),
    JSON.stringify(conversations.slice(0, 100)),
  );
};

export const createConversation = (facilityId, email, welcomeMsg) => {
  const conv = {
    id: `conv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: "New conversation",
    messages: welcomeMsg ? [welcomeMsg] : [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  const all = loadConversations(facilityId, email);
  all.unshift(conv);
  saveConversations(facilityId, email, all);
  return conv;
};

export const updateConversation = (facilityId, email, convId, updates) => {
  const all = loadConversations(facilityId, email);
  const idx = all.findIndex((c) => c.id === convId);
  if (idx === -1) return all;
  all[idx] = { ...all[idx], ...updates, updatedAt: new Date().toISOString() };
  saveConversations(facilityId, email, all);
  return all;
};

export const deleteConversation = (facilityId, email, convId) => {
  const all = loadConversations(facilityId, email).filter(
    (c) => c.id !== convId,
  );
  saveConversations(facilityId, email, all);
  return all;
};

export const autoTitle = (messages) => {
  const first = messages.find((m) => m.role === "user");
  if (!first) return "New conversation";
  const t = first.content.slice(0, 60);
  return t.length < first.content.length ? t + "…" : t;
};

const ANALYTICS_KEY = (fid) => `hyve-rag-analytics-${fid}`;

export const trackEvent = (facilityId, eventType, data = {}) => {
  try {
    const key = ANALYTICS_KEY(facilityId);
    const events = JSON.parse(localStorage.getItem(key) || "[]");
    events.push({
      type: eventType,
      timestamp: new Date().toISOString(),
      ...data,
    });
    if (events.length > 5000) events.splice(0, events.length - 5000);
    localStorage.setItem(key, JSON.stringify(events));
  } catch {
    /* storage full */
  }
};

// ═══════════════════════════════════════════════════════════════════
// ACTIVITY LOG — Today tab interactions
// ═══════════════════════════════════════════════════════════════════

export const ACTIVITY_KEY = "fieldiq_activity_log";

const todayStr = () => new Date().toISOString().slice(0, 10);

export const loadTodayActivity = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(ACTIVITY_KEY) || "[]");
    const today = todayStr();
    return Array.isArray(raw) ? raw.filter((a) => a.date === today) : [];
  } catch {
    return [];
  }
};

export const logActivity = (entry) => {
  try {
    const all = JSON.parse(localStorage.getItem(ACTIVITY_KEY) || "[]");
    all.unshift({ ...entry, date: todayStr(), ts: new Date().toISOString() });
    // Keep 500 entries max
    localStorage.setItem(ACTIVITY_KEY, JSON.stringify(all.slice(0, 500)));
  } catch {}
};

// ── Audio IndexedDB helpers ──────────────────────────────────────────
const openAudioDB = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open("fieldiq_audio", 1);
    req.onupgradeneeded = (e) =>
      e.target.result.createObjectStore("recordings");
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = () => reject(req.error);
  });

export const saveAudioBlob = async (key, blob) => {
  try {
    const db = await openAudioDB();
    const tx = db.transaction("recordings", "readwrite");
    tx.objectStore("recordings").put({ blob, mimeType: blob.type }, key);
  } catch {}
};

export const loadAudioBlob = async (key) => {
  try {
    const db = await openAudioDB();
    return await new Promise((resolve) => {
      const tx = db.transaction("recordings", "readonly");
      const req = tx.objectStore("recordings").get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};
